<template lang="pug">
  .b-spec-chat-screen(data-testid="chat-page" :class="{ 'chat-opened': !!activeChat }")
    ._list(data-testid="chat-list")
      Btn(v-if="canCreate" blue sm data-testid="chat-create" @click="$emit('create')") + Чат с поставщиком и заказчиком
      ._empty(v-if="!chats.length") Чатов пока нет
      button._list-item(
        v-for="chat in chats"
        :key="chat.id"
        type="button"
        data-testid="chat-list-item"
        :class="{ active: activeChat && activeChat.id === chat.id }"
        @click="$emit('select', chat)"
        )
        span._list-item-title(data-testid="chat-list-item-title") {{ chat.title }}
        span._list-item-last(data-testid="chat-list-item-last") {{ lastText(chat) }}
        span._list-item-count(data-testid="chat-list-item-count") сообщений: {{ chat.messagesCount }}

    ._chat(v-if="activeChat")
      ._header(data-testid="chat-header")
        button._back(type="button" data-testid="chat-back" title="К списку чатов" @click="$emit('back')") ‹ к списку
        span._header-title {{ activeChat.title }}
      ._feed(data-testid="chat-feed" ref="feed")
        ._empty(v-if="!messages.length" data-testid="chat-empty") Сообщений пока нет
        template(v-for="message in messages")
          ._event(v-if="message.authorType === 'system'" :key="message.id" data-testid="chat-event")
            span._time {{ dateTime(message.createdAt) }}
            span._event-text(data-testid="chat-event-text") {{ eventText(message) }}
            span._event-items
              template(v-for="(item, i) in eventItems(message)")
                button._event-item(
                  v-if="itemsClickable(message)"
                  :key="`l-${i}`"
                  type="button"
                  data-testid="chat-event-item"
                  @click="$emit('goto-item', item, message)"
                  ) {{ item.name }}
                span._event-item.plain(v-else :key="`t-${i}`" data-testid="chat-event-item") {{ item.name }}
              span._event-rest(v-if="eventRestCount(message)") и ещё {{ eventRestCount(message) }}
            button._event-request(
              v-if="message.eventData && message.eventData.request && requestClickable"
              type="button"
              data-testid="chat-event-request"
              @click="$emit('goto-request', message.eventData.request)"
              ) запрос КП
            a._event-file(
              v-for="file in message.files"
              :key="`f-${file.id}`"
              data-testid="chat-event-file"
              :href="file.url"
              target="_blank"
              rel="noopener noreferrer"
              ) {{ file.name }}

          ._message(v-else :key="message.id" data-testid="chat-message")
            span._message-author(data-testid="chat-message-author") {{ message.authorName || authorFallback(message) }}
            span._time {{ dateTime(message.createdAt) }}
            span._message-text(data-testid="chat-message-text") {{ message.text }}
            a._message-file(
              v-for="file in message.files"
              :key="`mf-${file.id}`"
              data-testid="chat-message-file"
              :href="file.url"
              target="_blank"
              rel="noopener noreferrer"
              ) {{ file.name }}

      ._form(v-if="canWrite")
        textarea._input(data-testid="chat-input" v-model="text" placeholder="Сообщение")
        label._attach
          | Прикрепить
          input._file(type="file" multiple data-testid="chat-attach-input" @change="onFiles")
        ._attached(v-if="files.length") файлов: {{ files.length }}
        Btn(blue sm data-testid="chat-send" :disabled="!canSend" @click="send") Отправить
</template>

<script>
import specChat from '@/model/specChat';
import { dateTime } from '@/utils/spec';

/**
 * Экран чатов, общий для дизайнера, поставщика и заказчика (ТЗ 6.10): слева список, справа лента
 * сообщений и системных событий, внизу поле ввода с вложениями.
 *
 * Роль задаётся свойствами: данные грузит страница, право создания трёхстороннего чата и
 * кликабельность позиций и запроса в событии передаются сюда, а не определяются здесь — так один
 * и тот же компонент используют три страницы (ТЗ 12.6). Сгенерировано Claude.
 */
export default {
  name: 'SpecChatScreen',
  props: {
    chats: { type: Array, default: () => [] },
    activeChat: Object,
    messages: { type: Array, default: () => [] },
    canCreate: Boolean,
    canWrite: { type: Boolean, default: true },
    /**
     * Позиции и запрос в событии кликабельны у дизайнера и поставщика (ТЗ 6.10). Вместе с позицией
     * наружу уходит и само событие (`goto-item(позиция, событие)`): у поставщика переход «к позиции»
     * ведёт в карточку запроса, а запрос называет именно то событие, по которому щёлкнули (8.3).
     */
    itemsAreLinks: Boolean,
    requestClickable: Boolean,
  },
  data() {
    return {
      text: '',
      files: [],
    };
  },
  computed: {
    canSend() {
      return this.text.trim().length > 0 || this.files.length > 0;
    },
  },
  methods: {
    dateTime,
    eventText: specChat.eventText,
    eventItems: specChat.eventItems,
    eventRestCount: specChat.eventRestCount,
    /** В событии об удалении позиций — только названия у всех ролей (ТЗ 6.10, 8.3). */
    itemsClickable(message) {
      return this.itemsAreLinks && message.eventType !== 'items_deleted';
    },
    lastText(chat) {
      if (!chat.lastMessageAt) return 'сообщений пока нет';
      const author = chat.lastMessageAuthor ? `${chat.lastMessageAuthor}, ` : '';
      return `${author}${dateTime(chat.lastMessageAt)}`;
    },
    authorFallback(message) {
      const map = { designer: 'Дизайнер', supplier: 'Поставщик', customer: 'Заказчик' };
      return map[message.authorType] ?? '';
    },
    onFiles(event) {
      this.files = Array.from(event.target.files ?? []);
    },
    send() {
      if (!this.canSend) return;
      this.$emit('send', { text: this.text.trim(), files: this.files });
      this.text = '';
      this.files = [];
    },
  },
};
</script>

<style scoped lang="scss">
.spec-chat-screen {
  display: flex;
  gap: 20px;
  align-items: flex-start;

  &__list {
    width: 320px;
    flex: 0 0 320px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__list-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
    font: inherit;
    text-align: left;
    cursor: pointer;

    &.active {
      border-color: var(--color-accent);
    }
  }

  &__list-item-title {
    font-weight: 700;
    font-size: 14px;
  }

  &__list-item-last,
  &__list-item-count {
    font-size: 11px;
    color: var(--color-middle-gray);
  }

  &__chat {
    flex: 1 1 0;
    min-width: 0;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
    display: flex;
    flex-direction: column;
    min-height: 420px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-super-light);
    font-weight: 700;
  }

  // на широких экранах список чатов всегда рядом, возврат нужен только когда он скрыт (ТЗ 6.10)
  &__back {
    display: none;
    border: none;
    background: none;
    font: inherit;
    color: var(--color-accent);
    cursor: pointer;
    padding: 0;
  }

  &__feed {
    flex-grow: 1;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 55vh;
    overflow-y: auto;
  }

  &__message,
  &__event {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 13px;
  }

  &__event {
    padding: 8px 12px;
    border-radius: 8px;
    background: #F1F5FA;
    color: var(--color-middle-gray);
  }

  &__event-text {
    flex-basis: 100%;
    color: var(--color-dark-gray);
  }

  &__event-items {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex-basis: 100%;
  }

  &__event-item {
    border: none;
    background: none;
    font: inherit;
    padding: 0;
    color: var(--color-accent);
    cursor: pointer;

    // у заказчика и в событии об удалении позиций — просто названия (ТЗ 6.10)
    &.plain {
      color: var(--color-dark-gray);
      cursor: default;
    }
  }

  &__event-request,
  &__event-file,
  &__message-file {
    border: none;
    background: none;
    font: inherit;
    padding: 0;
    color: var(--color-accent);
    cursor: pointer;
  }

  &__message-author {
    font-weight: 700;
    color: var(--color-dark-gray);
  }

  &__message-text {
    flex-basis: 100%;
    white-space: pre-wrap;
  }

  &__time {
    color: var(--color-middle-gray);
    font-size: 11px;
  }

  &__form {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 16px;
    border-top: 1px solid var(--color-super-light);
  }

  &__input {
    flex: 1 1 260px;
    min-height: 44px;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
    resize: vertical;
  }

  &__attach {
    color: var(--color-accent);
    cursor: pointer;
    font-size: 13px;
  }

  &__file {
    position: absolute;
    // (Claude, ревью этапа 9) нулевой размер: скрытое поле файла иначе расширяло документ на узких
    // экранах и давало горизонтальную прокрутку ленты (ТЗ 6.10, до 768 px)
    width: 0;
    height: 0;
    opacity: 0;
    visibility: hidden;
  }

  &__attached {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__empty {
    color: var(--color-middle-gray);
    font-size: 13px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    &__list {
      width: 100%;
      flex-basis: auto;
    }

    &.chat-opened &__list {
      display: none;
    }

    &__back {
      display: inline;
    }
  }
}
</style>
